import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  SortingState, 
  ColumnFiltersState,
} from "@tanstack/react-table";
import { toast } from "sonner";
import { Plus, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuthStore } from "@/app/store/authStore";
import { useQueryState, useQueryStates, parseAsInteger, parseAsString, parseAsArrayOf, createParser } from 'nuqs';
import { PaginatedResponse, ListQueryParams } from "@/app/types/api";
import { Party, PartyCreate, PartyUpdate, PartyGroup } from "@/app/types/party";
import { Site } from "@/app/types/site";
import { 
  listParties, 
  createParty, 
  updateParty, 
  deleteParty,
  listPartyGroups, 
} from "@/app/services/partyService";
import { listSites } from "@/app/services/siteService";
import { usePreferenceStore } from "@/app/store/preferenceStore";
import { columns as baseColumns } from "./components/columns";
import PartyForm from "./components/PartyForm";
import PartyDataTable from "./components/PartyDataTable";
import PartyGroupManagement from "./components/PartyGroupManagement";
import ClientUserDialog from "./components/ClientUserDialog";

const selectIsLoading = (state: { isLoading: boolean }): boolean => state.isLoading;

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return []
    const [field, direction] = value.split('-')
    return [{
      id: field,
      desc: direction === 'desc'
    }]
  },
  serialize: (value: SortingState) => {
    if (!value.length) return ''
    const sort = value[0]
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`
  }
})

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10)
    return isNaN(page) || page < 1 ? 0 : page - 1 // Convert 1-based to 0-based
  },
  serialize: (value: number) => {
    return String(value + 1) // Convert 0-based to 1-based
  }
})

const PartyPage = () => {
  const queryClient = useQueryClient();
  
  // Local State
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isClientUserDialogOpen, setIsClientUserDialogOpen] = useState(false);
  const [clientUserMode, setClientUserMode] = useState<'create' | 'reset'>('create');
  const [currentParty, setCurrentParty] = useState<Party | null>(null);
  
  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0))
  const [sorting, setSorting] = useQueryState('sortBy', sortingParser.withDefault([]))

  // Use nuqs for filter state management
  const [filterStates, setFilterStates] = useQueryStates({
    name: parseAsString,
    phone: parseAsString,
    email: parseAsString,
    gstin: parseAsString,
    gst_type: parseAsString,
    party_group_id: parseAsString,
    site_id: parseAsString,
    state: parseAsString,
  }, {
    // Use shallow routing to prevent full page reloads
    shallow: true,
  })
  
  // Build columnFilters from nuqs state for compatibility with existing code
  const columnFilters = useMemo<ColumnFiltersState>(() => {
    const filters: ColumnFiltersState = [];
    Object.entries(filterStates).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        filters.push({ id: key, value });
      }
    });
    return filters;
  }, [filterStates]);
  
  const [pageSize] = useState(10);
  const pagination = {
    pageIndex: page,
    pageSize,
    pageCount: 0
  };
  
  const isAuthLoading = useAuthStore(selectIsLoading);
  
  // Get user's default site preference
  const { defaultSiteId } = usePreferenceStore();
  
  // Construct query parameters for the API
  const queryParams = useMemo<ListQueryParams>(() => {
    const params: ListQueryParams = {
      skip: page * pageSize,
      limit: pageSize,
    };

    // Add sorting if defined
    if (sorting.length > 0) {
      params.sort_by = sorting[0].id;
      params.sort_dir = sorting[0].desc ? "desc" : "asc";
    }

    // Add filters if defined
    if (columnFilters.length > 0) {
      const filtersList: any[] = [];
      columnFilters.forEach((filter) => {
        if (filter.value !== undefined && filter.value !== null && filter.value !== '') {
          // Use icontains for text fields to enable wildcard search
          const textFields = ['name', 'phone', 'email', 'gstin'];
          const operator = textFields.includes(filter.id) ? 'icontains' : 'eq';

          filtersList.push({
            field: filter.id,
            op: operator,
            value: filter.value
          });
        }
      });

      if (filtersList.length > 0) {
        params.filters = JSON.stringify(filtersList);
      }
    }

    return params;
  }, [page, pageSize, sorting, columnFilters]);
  
  // Query to fetch parties
  const {
    data: partiesResponse = { items: [], total: 0 },
    isLoading: isPartiesLoading,
    isError: isPartiesError,
    error: partiesError,
    refetch: refetchParties,
  } = useQuery<PaginatedResponse<Party>>({
    queryKey: ['parties', queryParams],
    queryFn: () => listParties(queryParams),
  });
  
  // Query to fetch party groups
  const {
    data: groupsResponse,
    isLoading: isGroupsLoading,
  } = useQuery<PaginatedResponse<PartyGroup>>({
    queryKey: ['partyGroups'],
    queryFn: () => listPartyGroups({
      skip: 0,
      limit: 100,
      sort_by: 'name',
      sort_dir: 'asc'
    }),
  });
  
  // Query to fetch sites
  const {
    data: sitesResponse,
    isLoading: isSitesLoading,
  } = useQuery<PaginatedResponse<Site>>({
    queryKey: ['sites'],
    queryFn: () => listSites({
      skip: 0,
      limit: 100,
      sort_by: 'name',
      sort_dir: 'asc',
      show_all: true
    }),
  });
  
  // Calculate page count from response
  const pageCount = useMemo(() => {
    if (partiesResponse) {
      return Math.ceil(partiesResponse.total / pageSize);
    }
    return 0;
  }, [partiesResponse, pageSize]);

  // Setup event listeners for edit/delete/client-user actions from the table
  useEffect(() => {
    const handleEditParty = (event: CustomEvent<Party>) => {
      setCurrentParty(event.detail);
      setIsEditFormOpen(true);
    };

    const handleDeleteParty = (event: CustomEvent<Party>) => {
      setCurrentParty(event.detail);
      setIsDeleteDialogOpen(true);
    };

    const handleCreateClientUser = (event: CustomEvent<Party>) => {
      setCurrentParty(event.detail);
      setClientUserMode('create');
      setIsClientUserDialogOpen(true);
    };

    const handleResetClientPin = (event: CustomEvent<Party>) => {
      setCurrentParty(event.detail);
      setClientUserMode('reset');
      setIsClientUserDialogOpen(true);
    };

    document.addEventListener('edit-party', handleEditParty as EventListener);
    document.addEventListener('delete-party', handleDeleteParty as EventListener);
    document.addEventListener('create-client-user', handleCreateClientUser as EventListener);
    document.addEventListener('reset-client-pin', handleResetClientPin as EventListener);

    return () => {
      document.removeEventListener('edit-party', handleEditParty as EventListener);
      document.removeEventListener('delete-party', handleDeleteParty as EventListener);
      document.removeEventListener('create-client-user', handleCreateClientUser as EventListener);
      document.removeEventListener('reset-client-pin', handleResetClientPin as EventListener);
    };
  }, []);
  
  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: PartyCreate) => createParty(data),
    onSuccess: () => {
      toast.success('Party created successfully');
      queryClient.invalidateQueries({ queryKey: ['parties'] });
      setIsCreateFormOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create party: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: PartyUpdate }) => updateParty(id, data),
    onSuccess: () => {
      toast.success('Party updated successfully');
      queryClient.invalidateQueries({ queryKey: ['parties'] });
      setIsEditFormOpen(false);
      setCurrentParty(null);
    },
    onError: (error) => {
      toast.error(`Failed to update party: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteParty(id),
    onSuccess: () => {
      toast.success('Party deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['parties'] });
      setIsDeleteDialogOpen(false);
      setCurrentParty(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete party: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });
  
  // Event Handlers
  const handleCreateClick = () => {
    setIsCreateFormOpen(true);
  };

  const handleCreateSubmit = (formData: PartyCreate) => {
    createMutation.mutate(formData);
  };

  const handleEditSubmit = (formData: PartyUpdate) => {
    if (currentParty) {
      updateMutation.mutate({ id: currentParty.id, data: formData });
    }
  };

  const handleDeleteConfirm = () => {
    if (currentParty) {
      deleteMutation.mutate(currentParty.id);
    }
  };
  
  const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }) => {
    setPage(newPagination.pageIndex);
  };
  
  // Handle filter changes
  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    // Build the new filter states for nuqs
    const updates: Record<string, string | null> = {};

    // List of all possible filter keys
    const knownFilterKeys = ['name', 'phone', 'email', 'gstin', 'gst_type', 'party_group_id', 'site_id', 'state'];

    // First, reset all known keys to null
    knownFilterKeys.forEach(key => {
      updates[key] = null;
    });

    // Then add the active filters
    filters.forEach(filter => {
      const value = filter.value;
      updates[filter.id] = value !== undefined && value !== null && value !== '' ? String(value) : null;
    });

    // Update filters using nuqs
    setFilterStates(updates);

    // Reset to first page when filters change
    setPage(0);
  }, [setFilterStates, setPage]);
  
  // Parse party groups and sites for forms
  const partyGroups = groupsResponse?.items || [];
  const sites = sitesResponse?.items || [];
  
  // Create columns with dynamic party group and site options
  const columns = React.useMemo(() => {
    return baseColumns.map(col => {
      if (col.accessorKey === 'party_group_id' && col.meta?.variant === 'select') {
        return {
          ...col,
          meta: {
            ...col.meta,
            options: partyGroups.map(group => ({
              label: group.name,
              value: group.id.toString(),
            })),
          },
        };
      }
      if (col.accessorKey === 'site_id' && col.meta?.variant === 'select') {
        return {
          ...col,
          meta: {
            ...col.meta,
            options: sites.map(site => ({
              label: site.name,
              value: site.id.toString(),
            })),
          },
        };
      }
      return col;
    });
  }, [partyGroups, sites]);
  
  // --- Render Logic ---
  
  // Show loading indicator if auth state is loading
  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }
  
  // Handle error state
  if (isPartiesError) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading Parties</p>
        <p className="text-sm text-muted-foreground">
          {partiesError instanceof Error ? partiesError.message : 'An unknown error occurred.'}
        </p>
        <Button onClick={() => refetchParties()} variant="outline">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Retry
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Parties</h1>
        <Button onClick={handleCreateClick}>
          <Plus className="mr-2 h-4 w-4" /> Add Party
        </Button>
      </div>
      <p className="text-muted-foreground">
        Manage customer, vendor, and supplier records for your business.
      </p>
      
      {/* Party Groups Management */}
      <PartyGroupManagement 
        onGroupsChange={() => queryClient.invalidateQueries({ queryKey: ['partyGroups'] })}
      />
      
      {/* Parties Data Table */}
      <PartyDataTable
        data={partiesResponse}
        columns={columns}
        isLoading={isPartiesLoading || isGroupsLoading || isSitesLoading}
        partyGroups={partyGroups}
        pagination={{...pagination, pageCount}}
        sorting={sorting}
        columnFilters={columnFilters}
        onPaginationChange={handlePaginationChange}
        onSortingChange={setSorting}
        onColumnFiltersChange={handleFilterChange}
      />
      
      {/* Create Form */}
      <PartyForm
        open={isCreateFormOpen}
        onOpenChange={setIsCreateFormOpen}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        mode="create"
        partyGroups={partyGroups}
        sites={sites}
        defaultSiteId={defaultSiteId}
      />
      
      {/* Edit Form */}
      {currentParty && (
        <PartyForm
          open={isEditFormOpen}
          onOpenChange={(open) => {
            setIsEditFormOpen(open);
            if (!open) setCurrentParty(null);
          }}
          onSubmit={handleEditSubmit}
          defaultValues={currentParty}
          isLoading={updateMutation.isPending}
          mode="edit"
          partyGroups={partyGroups}
          sites={sites}
          defaultSiteId={defaultSiteId}
        />
      )}
      
      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the party &quot;{currentParty?.name}&quot;.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Client User Dialog */}
      <ClientUserDialog
        open={isClientUserDialogOpen}
        onOpenChange={setIsClientUserDialogOpen}
        party={currentParty}
        mode={clientUserMode}
      />
    </div>
  );
};

export default PartyPage; 